import haptic from '@/common/utils/haptics';
import { useGetUserActiveTurnsCount } from '@/common/apis/services/booking/getUserActiveTurnsCount';
import { useChatsUnreadCount } from '@/common/apis/services/hami/chatsUnreadCount';
import { useDoctorDeviceCached } from '@/common/hooks/useDoctorDeviceCached';
import { hasDoctorIdentity } from '@/common/utils/doctorIdentity';
import useApplication from '@/common/hooks/useApplication';
import useCustomize from '@/common/hooks/useCustomize';
import useResponsive from '@/common/hooks/useResponsive';
import { useHideOnScroll } from '@/common/hooks/useHideOnScroll';
import classNames from '@/common/utils/classNames';
import { useLoginModalContext } from '@/modules/login/context/loginModal';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useSearchStore } from '@/modules/search/store/search';
import { useRouter } from 'next/dist/client/router';
import { useEffect, useRef } from 'react';
import Text from '../../atom/text/text';
import CalenderIcon from '../../icons/calender';
import ChatIcon from '../../icons/chat';
import HomeIcon from '../../icons/home';
import SearchIcon from '../../icons/search';
import UserCircle from '../../icons/userCircle';

const unreadBadgeLabel = (count: number) => (count > 99 ? '+۹۹' : String(count));

const ChatIconWithBadge = ({ count }: { count: number }) => (
  <div className="relative">
    {count > 0 && (
      <span
        aria-label={`${count} پیام خوانده‌نشده`}
        className="absolute -top-1 -left-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-none text-white"
      >
        {unreadBadgeLabel(count)}
      </span>
    )}
    <ChatIcon />
  </div>
);

export const BottomNavigation = () => {
  const router = useRouter();
  const user = useUserInfoStore(state => state.info);
  const isLogin = useUserInfoStore(state => state.isLogin);
  const { handleOpenLoginModal } = useLoginModalContext();
  const turnsCount = useUserInfoStore(state => state.turnsCount);
  const setTurnsCount = useUserInfoStore(state => state.setTurnsCount);
  const getUserActiveTurnsCount = useGetUserActiveTurnsCount();
  const city = useSearchStore(state => state.city);
  const isApplication = useApplication();
  const customize = useCustomize(state => state.customize);
  const deviceCached = useDoctorDeviceCached();
  const isDoctor = hasDoctorIdentity(user, deviceCached);

  const isHiddenOnScroll = useHideOnScroll();
  const { isMobile } = useResponsive();
  // The nav is `md:hidden`: desktop visitors never see the badge, so they never ask Hami for it.
  const chatsUnread = useChatsUnreadCount(isLogin && isMobile);
  const wasInChatsRef = useRef(false);

  // Bars docked above the nav (FOLLOW_BOTTOM_NAV) slide down with it instead of floating over a gap.
  useEffect(() => {
    document.documentElement.toggleAttribute('data-bottom-nav-hidden', isHiddenOnScroll);
    return () => document.documentElement.removeAttribute('data-bottom-nav-hidden');
  }, [isHiddenOnScroll]);

  const isShowDashboard = !customize.partnerKey;

  const isDoctorShell = isDoctor && !customize.partnerKey;

  const servicesMenu = {
    name: 'پروفایل',
    icon: <UserCircle />,
    fillIcon: <UserCircle />,
    link: '/patient',
    pattern: '/patient',
    privateRoute: false,
    exact: false,
  };

  useEffect(() => {
    isLogin && handleGetTurnsCount();
    // Reading chats is what clears the badge: refresh right after leaving them, not on every navigation.
    const inChats = router.pathname.startsWith('/_/chats');
    if (wasInChatsRef.current && !inChats && isLogin && isMobile) chatsUnread.refetch();
    wasInChatsRef.current = inChats;
  }, [router.asPath, isLogin]);

  const handleGetTurnsCount = async () => {
    const { data } = await getUserActiveTurnsCount.mutateAsync();
    setTurnsCount({
      ...data.result,
    });
  };

  const doctorMenu = [
    {
      name: 'خانه',
      icon: <HomeIcon />,
      fillIcon: <HomeIcon isSolid />,
      link: '/_/',
      pattern: '/_',
      privateRoute: true,
      exact: false,
    },
    {
      name: 'مراجعین من',
      icon: <CalenderIcon />,
      fillIcon: <CalenderIcon isSolid />,
      link: '/dashboard/apps/drapp/appointments/',
      exact: true,
      pattern: '',
      privateRoute: true,
    },
    {
      name: 'جستجو',
      icon: <SearchIcon />,
      fillIcon: <SearchIcon />,
      link: `/s${city.en_slug !== 'ir' ? `/${city.en_slug}` : ''}`,
      pattern: '/s/[[...params]]',
      privateRoute: false,
      exact: false,
    },
    {
      name: 'گفت‌وگو‌ها',
      icon: <ChatIconWithBadge count={chatsUnread.count} />,
      fillIcon: <ChatIconWithBadge count={chatsUnread.count} />,
      link: `/_/chats`,
      pattern: ['/_/chats', '/_/chats/[[...hami]]'],
      privateRoute: true,
      exact: false,
    },
  ];

  const menus = [
    {
      name: 'خانه',
      icon: <HomeIcon />,
      fillIcon: <HomeIcon isSolid />,
      link: isApplication ? '/apphome' : '/',
      // both homes count as «خانه» (/apphome can also be opened outside the app shell)
      pattern: ['/', '/apphome'],
      privateRoute: false,
      exact: false,
    },
    {
      name: 'جستجو',
      icon: <SearchIcon />,
      fillIcon: <SearchIcon />,
      link: `/s${city.en_slug !== 'ir' ? `/${city.en_slug}` : ''}`,
      pattern: '/s/[[...params]]',
      privateRoute: false,
      exact: false,
    },
    {
      name: 'گفت‌وگو‌ها',
      icon: <ChatIconWithBadge count={chatsUnread.count} />,
      fillIcon: <ChatIconWithBadge count={chatsUnread.count} />,
      link: `/_/chats`,
      pattern: ['/_/chats', '/_/chats/[[...hami]]'],
      privateRoute: true,
      exact: false,
    },
    isDoctor
      ? {
          name: 'مراجعین من',
          icon: <CalenderIcon />,
          fillIcon: <CalenderIcon isSolid />,
          link: '/dashboard/apps/drapp/appointments/',
          exact: true,
          pattern: '',
          privateRoute: true,
        }
      : {
          name: 'نوبت‌های من',
          icon: (
            <div className="relative">
              {!!turnsCount.presence && (
                <div className="absolute flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full -top-1 -left-1">
                  {turnsCount.presence}
                </div>
              )}
              <CalenderIcon />
            </div>
          ),
          fillIcon: <CalenderIcon isSolid />,
          link: isShowDashboard ? '/dashboard/appointments/' : '/patient/appointments',
          ...(isShowDashboard && { exact: true }),
          pattern: '/patient/appointments',
          privateRoute: true,
          exact: false,
        },
    servicesMenu,
  ];

  const handleChangeRoute = (link: string, privateRoute: boolean) => {
    if (!isLogin && privateRoute) {
      handleOpenLoginModal({
        state: true,
        postLogin: () => router.push(link),
      });
      return;
    }
    router.push(link);
  };

  const navItems = isDoctorShell ? doctorMenu : menus;

  const renderNavItem = (
    { icon, fillIcon, name, link, privateRoute, pattern, exact }: (typeof navItems)[number],
    key: string,
    opts?: { flex?: boolean },
  ) => {
    const active = exact
      ? router.asPath === link
      : router.pathname === pattern || (Array.isArray(pattern) && pattern.includes(router.pathname));

    return (
      <div
        key={key}
        role="link"
        tabIndex={0}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          if (!active) haptic('selection');
          handleChangeRoute(link, privateRoute);
        }}
        onKeyDown={e => e.key === 'Enter' && handleChangeRoute(link, privateRoute)}
        className={classNames(
          'press flex cursor-pointer flex-col items-center justify-center space-y-1 font-medium text-slate-700 transition-all scale-95',
          opts?.flex ? 'min-w-0 flex-1' : 'w-[70px]',
          {
            '!text-primary font-bold scale-100': active,
          },
        )}
      >
        {fillIcon && active ? fillIcon : icon}
        <Text fontSize="xs" className="whitespace-nowrap">
          {name}
        </Text>
      </div>
    );
  };

  return (
    <div
      className={classNames(
        'fixed bottom-0 left-0 z-40 flex items-center justify-between w-full h-[calc(4rem_+_env(safe-area-inset-bottom))] px-4 pb-[env(safe-area-inset-bottom)] bg-[#F9F9F9] border-t print:hidden md:hidden border-[#d5d8db] transition-transform will-change-transform',
        // Material motion: exit 175ms accelerate, enter 225ms decelerate.
        isHiddenOnScroll
          ? 'translate-y-full duration-[175ms] ease-[cubic-bezier(0.4,0,1,1)]'
          : 'translate-y-0 duration-[225ms] ease-[cubic-bezier(0,0,0.2,1)]',
      )}
    >
      {navItems.map((item, index) => renderNavItem(item, `${item.name}-${index}`))}
    </div>
  );
};

export default BottomNavigation;
